import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Seo from '../../components/Seo';
import { PageLoader, ErrorState, Breadcrumbs } from '../../components/ui';
import GalleryGrid from '../../components/GalleryGrid';
import MediaCarousel from '../../components/MediaCarousel';
import FacebookPostEmbed from '../../components/FacebookPostEmbed';
import ShareBar from '../../components/ShareBar';
import { useAsync } from '../../lib/useAsync';
import { sanitize } from '../../lib/sanitize';
import { activitiesApi } from '../../api/resources';
import { assetUrl } from '../../api/client';
import { formatDate, truncate, statusMeta } from '../../lib/format';

export default function ActivityDetail() {
  const { slug } = useParams();
  const { data: activity, loading, error, reload } = useAsync(() => activitiesApi.get(slug), [slug]);
  const [carouselIndex, setCarouselIndex] = useState(null);

  if (loading) return <PageLoader label="Loading activity…" />;
  if (error) return <div className="container-page py-16"><ErrorState error={error} onRetry={reload} /></div>;
  if (!activity) return null;

  const gallery = activity.gallery || [];
  // Facebook post embeds are shown inline below the body; everything else
  // stays in the photo grid + carousel (both get the same filtered array, so
  // carousel indexes line up with the grid).
  const posts = gallery.filter((g) => g.provider === 'facebook_post');
  const media = gallery.filter((g) => g.provider !== 'facebook_post');
  const heroImg = assetUrl(activity.first_image_url) || assetUrl(activity.image_path);
  const banner = heroImg || '/img/community.jpg';
  const status = activity.status ? statusMeta(activity.status) : null;

  return (
    <>
      <Seo
        title={activity.title}
        description={activity.summary || truncate(activity.description, 160)}
        image={heroImg}
        type="article"
      />

      <article className="pb-16">
        <header className="relative overflow-hidden">
          <img
            src={banner}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="hero-overlay" />
          <div className="container-page relative max-w-4xl py-20 sm:py-28">
            <div className="mb-4 flex flex-wrap items-center gap-3">
              {status && <span className={`pill ${status.cls}`}>{status.label}</span>}
              {activity.occurred_on && (
                <span className="text-sm text-cream-200">{formatDate(activity.occurred_on)}</span>
              )}
            </div>
            <h1 className="font-serif text-3xl font-bold leading-tight text-white sm:text-5xl">
              {activity.title}
            </h1>
          </div>
        </header>

        <div className="container-page max-w-3xl pt-8">
          <Breadcrumbs
            items={[{ label: 'Home', to: '/' }, { label: 'Activities', to: '/activities' }, { label: activity.title }]}
          />

          <ShareBar title={activity.title} className="mt-6" />

          {activity.summary && (
            <div className="mt-6 rounded-2xl bg-cream-100 p-6 ring-1 ring-cream-300">
              <span aria-hidden="true" className="font-display text-3xl leading-none text-gold-500">
                “
              </span>
              <p className="mt-1 text-lg italic leading-relaxed text-ink-soft">{activity.summary}</p>
            </div>
          )}

          <div
            className="rich-content mt-6"
            dangerouslySetInnerHTML={{ __html: sanitize(activity.description) || '<p>No details available.</p>' }}
          />

          {posts.length > 0 && (
            <section className="mt-10" aria-labelledby="activity-facebook-heading">
              <h2 id="activity-facebook-heading" className="heading-brand text-xl">
                From our Facebook Page
              </h2>
              <div className="mt-4 space-y-6">
                {posts.map((p) => (
                  <FacebookPostEmbed key={p.id} embedUrl={p.embed_url} url={p.url} title={`Facebook post: ${activity.title}`} />
                ))}
              </div>
            </section>
          )}

          {media.length > 0 && (
            <section className="mt-10" aria-labelledby="gallery-heading">
              <h2 id="gallery-heading" className="heading-brand text-xl">
                Gallery
              </h2>
              <p className="mt-1 text-sm text-ink-muted">
                {media.length} {media.length === 1 ? 'item' : 'items'} — select any to view.
              </p>
              <div className="mt-4">
                <GalleryGrid items={media} onOpen={setCarouselIndex} />
              </div>
            </section>
          )}

          <div className="mt-10">
            <Link to="/activities" className="btn-ghost">
              ← Back to all activities
            </Link>
          </div>
        </div>
      </article>

      <MediaCarousel
        items={media}
        index={carouselIndex}
        onClose={() => setCarouselIndex(null)}
        onIndexChange={setCarouselIndex}
      />
    </>
  );
}
